import type React from "react";
import { useRef } from "react";
import { Card } from "primereact/card";
import { useDropzone } from "react-dropzone";
import CustomeButton from "~/components/Button";
import ToastMessage from "~/components/ToastMessage";
import {
  useImportPayslipEvents,
  validateImportFile,
} from "./useImportPayslipEvents";
import DropzoneArea from "./components/DropzoneArea";
import FileInfoCard from "./components/FileInfoCard";
import ValidationErrorsTable from "./components/ValidationErrorsTable";
import "./import-payslip-events.scss";

const ImportPayslipEvents: React.FC = () => {
  const toastRef = useRef<{
    show: (options: {
      severity?: string;
      summary?: string;
      detail?: string;
      life?: number;
    }) => void;
  } | null>(null);

  const {
    fileState,
    setFileState,
    setFile,
    clearFile,
    resetValidation,
    validationStatus,
    validationErrors,
    parsedRowCount,
    importMutation,
  } = useImportPayslipEvents();

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0] ?? null;
    const basicError = validateImportFile(file ?? null);

    if (file) {
      console.log(
        "[ImportPayslipEvents] File selected:",
        file.name,
        `(${(file.size / 1024).toFixed(1)} KB)`,
      );
    }

    if (file && basicError) {
      setFileState({ file, error: basicError });
      resetValidation();
      return;
    }

    setFile(file);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".csv",
      ],
    },
    maxSize: 25 * 1024 * 1024,
    noClick: true,
  });

  const handleImport = async () => {
    try {
      if (validationStatus !== "valid" || !fileState.file) {
        toastRef.current?.show({
          severity: "error",
          summary: "Validation Error",
          detail: "Please fix validation errors before importing.",
        });
        return;
      }

      const response = await importMutation.mutateAsync();

      const note =
        response?.data?.note ??
        response?.message ??
        "Import started. You will receive a notification when it's done.";

      toastRef.current?.show({
        severity: "info",
        summary: "Import Started",
        detail: note,
        life: 5000,
      });

      clearFile();
    } catch (error) {
      const message =
        (error as Error)?.message ||
        "The import could not be started. Please try again.";

      toastRef.current?.show({
        severity: "error",
        summary: "Import Failed",
        detail: message,
      });
    }
  };

  const isLoading = importMutation.isPending;
  const showDropzone =
    !fileState.file ||
    validationStatus === "idle" ||
    validationStatus === "validating" ||
    validationStatus === "invalid" ||
    !!fileState.error;
  const showFileInfo = fileState.file && validationStatus === "valid";
  const showErrors =
    fileState.file &&
    validationStatus === "invalid" &&
    validationErrors.length > 0;

  return (
    <>
      <Card className="gen-card mt-0 h-full">
        <div className="general-info-container import-payslip-events">
          <div className="import-payslip-events__header">
            <div className="import-payslip-events__title-block">
              <h2 className="profile-heading">Import Payslip Events</h2>
              <p className="profile-para">
                Select the Payslip CSV file to create Payroll milestones and
                tasks.
              </p>
              <p className="profile-para import-payslip-events__note">
                <strong>Note:</strong> Only entities with the Payroll+ service
                line active can import events. If any entity in the file does
                not have Payroll+ active, the import will fail.
              </p>
            </div>
            <div className="flex align-items-center">
              <CustomeButton
                label={isLoading ? "Starting Import..." : "Import Events"}
                variant="contained"
                onClick={handleImport}
                disabled={
                  isLoading || validationStatus !== "valid" || !fileState.file
                }
                icon={isLoading ? undefined : "pi pi-upload"}
                iconPos="right"
                loading={isLoading}
              />
            </div>
          </div>

          {showDropzone && (
            <DropzoneArea
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              isDragActive={isDragActive}
              open={open}
            />
          )}

          {validationStatus === "validating" && fileState.file && (
            <p className="import-payslip-events__validating">
              Validating file...
            </p>
          )}

          {fileState.file && fileState.error && (
            <p className="import-payslip-events__error">{fileState.error}</p>
          )}

          {showFileInfo && fileState.file && (
            <FileInfoCard
              fileName={fileState.file.name}
              fileSizeBytes={fileState.file.size}
              rowCount={parsedRowCount}
              onRemove={clearFile}
            />
          )}

          {showErrors && <ValidationErrorsTable errors={validationErrors} />}

          <div className="import-payslip-events__footer">
            <span className="import-payslip-events__format-text">
              Supported formats: CSV only
            </span>
            <span className="import-payslip-events__size-text">
              Maximum file size: 25MB
            </span>
          </div>
        </div>
      </Card>

      <ToastMessage ref={toastRef} />
    </>
  );
};

export default ImportPayslipEvents;
